import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Drawer,
  CircularProgress,
  Alert,
  Paper,
  useTheme,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

import { useChatLogs } from '../../hooks/chat/useChatLogs';
import ChatLogsTable from '../../components/chatLogs/ChatLogsTable';
import { ChatLogsT } from '../../types/chat';

import { useChatBookingLogs } from '../../hooks/chat/useChatBookingLogs';
import ChatBookingLogsTable from '../../components/chatLogs/ChatBookingLogsTable';
import { ChatLogBooking } from '../../types/chat_booking';

import EmptyStateIcon from '../../assets/images/emptyState-illus.svg';
import ChatLogDetail from './ChatLogDetail';
import { BookingSearchBar } from '../../components/chatLogs/BookingSearchBar';
import ChatSearchSummary from '../../components/chatLogs/ChatSearchSummary';
import ChatTopMatches from '../../components/chatLogs/ChatTopMatches';
import { ThinkingLoader } from '../../components/chatLogs/ThinkingLoader';
import { ChatLogsFilter } from '../../components/chatLogs/ChatLogsFilter';

const ChatLogsView: React.FC = () => {
  const selectedOrganisation = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );
  const isBookingEnabled = selectedOrganisation?.isBookingEnabled ?? false;

  const rag = useChatLogs();
  const booking = useChatBookingLogs();
  const src = isBookingEnabled ? booking : rag;

  const { chatLogs, isLoading, error, unreadCount } = src;

  const [selectedLog, setSelectedLog] = useState<ChatLogsT | ChatLogBooking | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [activeTab, setActiveTab] = useState<'chat' | 'voice'>('chat');

  const isVoiceFilter = activeTab === 'voice';

  const handlePageChange = (newPage: number) => {
    console.log('Page 1');
    booking.fetchAll(newPage, booking.pageSize, isVoiceFilter);
    console.log('Page 2');
  };
  const handleRowsPerPageChange = (newPageSize: number) => {
    console.log('Page 3');
    booking.fetchAll(1, newPageSize, isVoiceFilter);
    console.log('Page 4');
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: 'chat' | 'voice') => {
    setActiveTab(newValue);
    booking.fetchAll(1, booking.pageSize, newValue === 'voice');
  };

  const openLogById = async (chatId: string) => {
    if (!isBookingEnabled) return;
    const log = await booking.fetchById(chatId);
    if (log) {
      setSelectedLog(log);
      setIsDetailOpen(true);
      booking.updateViewed(log.uuid);
    }
  };

  const handleSelect = (log: ChatLogsT | ChatLogBooking) => {
    setSelectedLog(log);
    setIsDetailOpen(true);
    if (isBookingEnabled) {
      booking.updateViewed((log as ChatLogBooking).uuid);
    } else {
      rag.updateChatLog(log as ChatLogsT);
    }
  };

  useEffect(() => {
    if (!selectedOrganisation) return;
    if (isBookingEnabled) {
      console.log('Page 5');
      booking.fetchAll(1, 10, isVoiceFilter);
      console.log('Page 6');
    } else {
      console.log('Page 7');
      rag.refetch?.();
      console.log('Page 8');
    }
  }, [selectedOrganisation?.id, isBookingEnabled]);

  const theme = useTheme();

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (chatLogs === undefined || (isLoading && chatLogs.length === 0)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Logs
          </Typography>
          {unreadCount > 0 && <Typography variant="subtitle1">({unreadCount} unread)</Typography>}
        </Box>

        {!isBookingEnabled && rag.chatLogs.length > 0 && (
          <Box sx={{ backgroundColor: theme.palette.background.paper }}>
            <ChatLogsFilter
              chatLogs={rag.chatLogs}
              setMonthYear={rag.setMonthYear}
              selectedMonthYear={rag.selectedMonthYear}
            />
          </Box>
        )}

        {isBookingEnabled && (
          <BookingSearchBar
            key={searchResetKey}
            onSearch={(term, from, to) =>
              booking.search({ searchTerm: term, startDate: from, endDate: to })
            }
          />
        )}
      </Box>

      {isBookingEnabled && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Chat" value="chat" />
            <Tab label="Voice" value="voice" />
          </Tabs>
        </Box>
      )}

      {booking.isSearching ? (
        <ThinkingLoader />
      ) : booking.searchResults ? (
        <Paper elevation={0} sx={{ mb: 3, p: 2 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">Search Results</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                booking.resetSearch();
                setSearchResetKey(prev => prev + 1); // triggers BookingSearchBar reset
              }}
            >
              Reset
            </Button>
          </Box>
          <ChatSearchSummary summary={booking.searchResults.summary} />
          <ChatTopMatches
            topMatches={booking.searchResults.topMatches ?? []}
            onMatchClick={openLogById}
          />
        </Paper>
      ) : chatLogs === undefined ||
        (isBookingEnabled && chatLogs.length === 0) ||
        !chatLogs.length ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            px: 3,
            py: 6,
          }}
        >
          <img className="w-28 mx-auto" src={EmptyStateIcon} alt="No data" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            No chats found
          </Typography>
        </Box>
      ) : isBookingEnabled ? (
        <ChatBookingLogsTable
          chatLogs={chatLogs as ChatLogBooking[]}
          onSelect={handleSelect}
          pagination={{
            page: booking.page,
            pageSize: booking.pageSize,
            total: booking.total,
            totalPages: booking.totalPages,
          }}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          isLoading={isLoading}
        />
      ) : (
        <ChatLogsTable
          chatLog={chatLogs as ChatLogsT[]}
          onSelect={handleSelect as (log: ChatLogsT) => void}
        />
      )}

      <Drawer
        anchor="right"
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '70%', lg: '50%', xl: '45%' },
            p: 0,
            py: { xs: 2, sm: 2 },
            px: { xs: 2, sm: 3 },
          },
        }}
      >
        {selectedLog && (
          <ChatLogDetail chatLog={selectedLog as any} onClose={() => setIsDetailOpen(false)} />
        )}
      </Drawer>
    </Box>
  );
};

export default ChatLogsView;
